/* ==========================================================================
   Intentional Realignment — behaviour for the custom design sections.

   Custom elements only, no framework and no third-party runtime. Each element
   degrades to readable static markup when JavaScript is unavailable.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- helpers */

  /** Close every open <ir-nav-item> except the one passed in. */
  function closeOtherMenus(except) {
    document.querySelectorAll('ir-nav-item').forEach(function (item) {
      if (item !== except && typeof item.close === 'function') item.close();
    });
  }

  /* ------------------------------------------------------------- <ir-nav-item> */

  /**
   * A header nav disclosure: a button plus the panel it controls.
   * Closes on outside click, on Escape, and when another menu opens.
   */
  class IrNavItem extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-ir-nav-toggle]');
      this.panel = this.querySelector('[data-ir-nav-panel]');
      if (!this.button || !this.panel) return;

      this.onToggle = this.onToggle.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.button.addEventListener('click', this.onToggle);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get open() {
      return this.button.getAttribute('aria-expanded') === 'true';
    }

    onToggle(event) {
      event.preventDefault();
      if (this.open) {
        this.close();
      } else {
        closeOtherMenus(this);
        this.show();
      }
    }

    show() {
      this.button.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
    }

    close() {
      if (!this.button) return;
      this.button.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.close();
      this.button.focus();
    }
  }

  /* --------------------------------------------------------- <ir-overflow-nav> */

  /**
   * Header nav that keeps the wordmark centred.
   *
   * Shows as many top-level links as fit on one row and moves the rest into a
   * "More" disclosure, re-measuring whenever the header resizes. With no
   * JavaScript every link simply renders inline.
   */
  class IrOverflowNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-ir-nav-primary]'));
      this.more = this.querySelector('[data-ir-more]');
      this.morePanel = this.querySelector('[data-ir-more-panel]');
      if (this.items.length === 0 || !this.more || !this.morePanel) return;

      this.layout = this.layout.bind(this);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.layout);
        this.observer.observe(this.parentElement || this);
      } else {
        window.addEventListener('resize', this.layout);
      }

      // Fonts land after first paint and change every measurement.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.layout);
      }

      this.layout();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.layout);
    }

    /** Space the nav may occupy without pushing into the wordmark. */
    get available() {
      const parent = this.parentElement;
      return parent ? parent.clientWidth : this.clientWidth;
    }

    layout() {
      // The compact drawer owns the header at narrow widths; skip measuring.
      if (this.offsetParent === null) return;

      // Start from everything visible so the measurement is honest.
      this.items.forEach((item) => {
        item.hidden = false;
      });
      this.more.hidden = true;
      this.morePanel.replaceChildren();

      if (this.scrollWidth <= this.available) return;

      // Something has to give: reveal "More", then retract from the end.
      this.more.hidden = false;

      const overflowed = [];
      for (let i = this.items.length - 1; i >= 0; i--) {
        if (this.scrollWidth <= this.available) break;
        this.items[i].hidden = true;
        overflowed.unshift(this.items[i]);
      }

      overflowed.forEach((item) => {
        this.morePanel.append(...IrOverflowNav.linksFor(item));
      });

      // Everything collapsed and it still does not fit — drop "More" too rather
      // than leave a lone button beside an empty row.
      if (overflowed.length === this.items.length && this.scrollWidth > this.available) {
        this.more.hidden = this.morePanel.childElementCount === 0;
      }
    }

    /** Flatten one nav item into plain anchors for the "More" panel. */
    static linksFor(item) {
      if (item.matches('a')) {
        const a = document.createElement('a');
        a.href = item.getAttribute('href') || '#';
        a.textContent = item.textContent.trim();
        return [a];
      }

      const panel = item.querySelector('[data-ir-nav-panel]');
      if (!panel) return [];

      return Array.from(panel.querySelectorAll('a')).map((source) => {
        const a = document.createElement('a');
        a.href = source.getAttribute('href') || '#';
        a.textContent = source.textContent.trim();
        return a;
      });
    }
  }

  /* -------------------------------------------------------------- <ir-carousel> */

  /**
   * Generic one-at-a-time carousel.
   *
   * Slides are `[data-ir-slide]`, controls are `[data-ir-prev]`,
   * `[data-ir-next]` and `[data-ir-dot]`. Wraps at both ends.
   */
  class IrCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-ir-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-ir-dot]'));
      if (this.slides.length === 0) return;

      this.index = 0;

      const prev = this.querySelector('[data-ir-prev]');
      const next = this.querySelector('[data-ir-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.go(i));
      });

      this.go(0);
    }

    step(delta) {
      const count = this.slides.length;
      this.go((this.index + delta + count) % count);
    }

    go(index) {
      this.index = index;

      this.slides.forEach((slide, i) => {
        slide.hidden = i !== index;
      });

      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.dispatchEvent(
        new CustomEvent('ir:carousel:change', { detail: { index }, bubbles: true })
      );
    }
  }

  /* ---------------------------------------------------------- <ir-media-gallery> */

  /**
   * Product media gallery: thumbnails swap the main image.
   * Also exposes prev/next so it doubles as the hero card gallery.
   */
  class IrMediaGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-ir-stage]');
      this.thumbs = Array.from(this.querySelectorAll('[data-ir-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-ir-dot]'));
      if (!this.stage || this.thumbs.length === 0) return;

      this.index = 0;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.go(i));
      });

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.go(i));
      });

      const prev = this.querySelector('[data-ir-prev]');
      const next = this.querySelector('[data-ir-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.go(0);
    }

    step(delta) {
      const count = this.thumbs.length;
      this.go((this.index + delta + count) % count);
    }

    go(index) {
      const thumb = this.thumbs[index];
      if (!thumb) return;

      this.index = index;

      const src = thumb.getAttribute('data-ir-src');
      const srcset = thumb.getAttribute('data-ir-srcset');
      const alt = thumb.getAttribute('data-ir-alt');

      if (src) {
        this.stage.src = src;

        /*
         * A stage rendered by `image_tag` carries a srcset, and srcset outranks
         * src, so setting src alone leaves the browser painting the first image.
         * Swap the srcset too, or clear it so src is all that is left to obey.
         */
        if (srcset) this.stage.srcset = srcset;
        else this.stage.removeAttribute('srcset');
      }

      if (alt !== null) this.stage.alt = alt;

      this.thumbs.forEach((el, i) => {
        el.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.dots.forEach((el, i) => {
        el.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  /* ------------------------------------------------------------ <ir-quantity> */

  /** Minus/plus stepper bound to a number input. */
  class IrQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      const minus = this.querySelector('[data-ir-minus]');
      const plus = this.querySelector('[data-ir-plus]');

      if (minus) minus.addEventListener('click', () => this.nudge(-1));
      if (plus) plus.addEventListener('click', () => this.nudge(1));
    }

    nudge(delta) {
      const min = parseInt(this.input.min, 10) || 1;
      const current = parseInt(this.input.value, 10) || min;
      const next = Math.max(min, current + delta);

      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ------------------------------------------------------ sticky measurements */

  /**
   * Publishes the announcement bar and header heights as CSS variables.
   *
   * `#header-group` is pinned with a negative offset equal to the announcement
   * height, so the strip scrolls away and the header lands flush at the top.
   * Anything else that needs to sit under the header (the ingredient pages'
   * buy bar) reads `--ir-header-h` rather than guessing a fixed number.
   */
  function measureStickyChrome() {
    const announcement = document.querySelector('.ir-announcement');
    const header = document.querySelector('.ir-header');
    const root = document.documentElement;

    if (announcement) {
      root.style.setProperty('--ir-announcement-h', Math.round(announcement.offsetHeight) + 'px');
    } else {
      root.style.setProperty('--ir-announcement-h', '0px');
    }

    if (header) {
      root.style.setProperty('--ir-header-h', Math.round(header.offsetHeight) + 'px');
    }
  }

  function watchStickyChrome() {
    measureStickyChrome();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(measureStickyChrome);
      const announcement = document.querySelector('.ir-announcement');
      const header = document.querySelector('.ir-header');
      if (announcement) ro.observe(announcement);
      if (header) ro.observe(header);
    } else {
      window.addEventListener('resize', measureStickyChrome);
    }

    // Fonts change the header's height after first paint.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureStickyChrome);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchStickyChrome);
  } else {
    watchStickyChrome();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', measureStickyChrome);
  document.addEventListener('shopify:section:unload', measureStickyChrome);

  /* -------------------------------------------------------------- registration */

  const registry = {
    'ir-nav-item': IrNavItem,
    'ir-overflow-nav': IrOverflowNav,
    'ir-carousel': IrCarousel,
    'ir-media-gallery': IrMediaGallery,
    'ir-quantity': IrQuantity,
  };

  Object.keys(registry).forEach(function (tag) {
    if (!customElements.get(tag)) customElements.define(tag, registry[tag]);
  });
})();
